
/**
 * Generate a unique ID
 */
export const uid = () => crypto.randomUUID();

/**
 * Truncate text to N words with ellipsis
 */
export const truncateWords = (text, count = 5) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= count) return text;
  return words.slice(0, count).join(" ") + "…";
};

/**
 * Format timestamp to readable time
 */
export const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

/**
 * Format Indian-style price (Lakhs / Crores)
 */
export const formatPrice = (price) => {
  if (!price) return "Price on request";

  const s = String(price);
  if (s.includes("₹") || /\bCr\b|\bL\b/i.test(s)) return s;

  const cleaned = s.replace(/[₹,]/g, "");
  const num = Number(cleaned);

  if (isNaN(num)) return price;

  if (num >= 10000000) {
    return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  }

  if (num >= 100000) {
    return `₹ ${(num / 100000).toFixed(2)} L`;
  }

  return `₹ ${num.toLocaleString("en-IN")}`;
};

/**
 * Debounce function
 */
export const debounce = (fn, ms = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

export const parseMarkdown = (text) => {
  if (!text) return "";

  const codeBlocks = [];
  text = text.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(code.trim());
    return `%%CODE_BLOCK_${idx}%%`;
  });

  const inlineCodes = [];
  text = text.replace(/`([^`]+)`/g, (_, code) => {
    const idx = inlineCodes.length;
    inlineCodes.push(code);
    return `%%INLINE_CODE_${idx}%%`;
  });

  text = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  text = text.replace(/^####\s+(.+)$/gm, '<h4 class="ai-h4">$1</h4>');
  text = text.replace(/^###\s+(.+)$/gm,  '<h3 class="ai-h3">$1</h3>');
  text = text.replace(/^##\s+(.+)$/gm,   '<h3 class="ai-h3">$1</h3>');
  text = text.replace(/^#\s+(.+)$/gm,    '<h3 class="ai-h3">$1</h3>');

  text = text.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  text = text.replace(/\*\*(.+?)\*\*/g,       "<strong>$1</strong>");
  text = text.replace(/\*([^*\n]+?)\*/g,        "<em>$1</em>");

  text = text.replace(/^[\-\*]\s+(.+)$/gm, "%%BULLET_LI%%$1%%END_LI%%");

  text = text.replace(/^\d+\.\s+(.+)$/gm, "%%NUMBER_LI%%$1%%END_LI%%");

  text = text.replace(/(%%BULLET_LI%%[\s\S]*?%%END_LI%%(?:\n%%BULLET_LI%%[\s\S]*?%%END_LI%%)*)/g, (block) => {
    const items = block.replace(/%%BULLET_LI%%/g, "<li>").replace(/%%END_LI%%/g, "</li>");
    return '<ul class="ai-list">' + items.replace(/\n/g, "") + "</ul>";
  });

  text = text.replace(/(%%NUMBER_LI%%[\s\S]*?%%END_LI%%(?:\n%%NUMBER_LI%%[\s\S]*?%%END_LI%%)*)/g, (block) => {
    const items = block.replace(/%%NUMBER_LI%%/g, "<li>").replace(/%%END_LI%%/g, "</li>");
    return '<ol class="ai-list ai-ol">' + items.replace(/\n/g, "") + "</ol>";
  });

  text = text.replace(/%%BULLET_LI%%/g, "<li>").replace(/%%NUMBER_LI%%/g, "<li>").replace(/%%END_LI%%/g, "</li>");

  text = text.replace(/^---$/gm, "<hr/>");

  const tableRegex = /^(\|.+\|[ \t]*\n)(\|[-: |]+\|[ \t]*\n)((\|.+\|[ \t]*\n?)*)/gm;
  text = text.replace(tableRegex, (match, header, separator, body) => {
    const parseRow = (row) => row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());
    const headers = parseRow(header);
    const rows    = body.trim().split("\n").filter(r => r.trim().startsWith("|"));
    const thead   = "<thead><tr>" + headers.map(h => `<th>${h}</th>`).join("") + "</tr></thead>";
    const tbody   = "<tbody>" + rows.map(r => {
      const cells = parseRow(r);
      return "<tr>" + cells.map(c => `<td>${c}</td>`).join("") + "</tr>";
    }).join("") + "</tbody>";
    return `<div class="ai-table-wrap"><table class="ai-table">${thead}${tbody}</table></div>\n`;
  });

  text = text.replace(/\n{4,}/g, "\n\n");
  text = text.replace(/\n{2,}/g, "<br/><br/>");
  text = text.replace(/\n/g, "<br/>");

  text = text.replace(/%%CODE_BLOCK_(\d+)%%/g, (_, i) =>
      '<pre class="code-block"><code>' + codeBlocks[parseInt(i)] + '</code></pre>'
  );
  text = text.replace(/%%INLINE_CODE_(\d+)%%/g, (_, i) =>
      '<code class="inline-code">' + inlineCodes[parseInt(i)] + '</code>'
  );

  return text;
};


export const isShortcut = (e, key, mod = "ctrl") => {
  const modKey = mod === "ctrl" ? e.ctrlKey || e.metaKey : e[`${mod}Key`];
  return modKey && e.key.toLowerCase() === key.toLowerCase();
};
